import moment, { Moment } from 'moment';
import { NTab, NTabPane, NTabs } from 'naive-ui';
import { mapState, mapStores } from "pinia";
import { defineComponent } from "vue";

import BasePage from '../../core/base-page';
import DashboardHeader from './dashboard-header/dashboard-header.vue';

import { useDashboardStore } from "../../store/dashboard.store";
import { useStationStore } from "../../store/station.store";

import WidgetStationTable from '../../components/widget-device-table/widget-device-table.vue';
import WidgetMap from '../../components/widget-map/widget-map.vue';

import DashboardEventsChart from '~/components/dashboard-events-chart/dashboard-events-chart.vue';
import DashboardOfflineChart from '~/components/dashboard-offline-chart/dashboard-offline-chart.vue';
import WidgetIndicatorGasAlarm from '~/components/widget-indicator-gas-alarm/widget-indicator-gas-alarm.vue';
import WidgetIndicatorOffline from '~/components/widget-indicator-offline/widget-indicator-offline.vue';
import WidgetIndicatorOk from '~/components/widget-indicator-ok/widget-indicator-ok.vue';
import WidgetIndicatorReallyOffline from '~/components/widget-indicator-really-offline/widget-indicator-really-offline.vue';
import WidgetIndicatorTrouble from '~/components/widget-indicator-trouble/widget-indicator-trouble.vue';
import WidgetIndicator from '~/components/widget-indicator/widget-indicator.vue';
import { DashboardStoreSocketError, StoreError } from "~/exceptions/store.error";
import { LocationQuery } from 'vue-router';

enum TabEnum {
    ALARMS = 'ALARMS',
    DEVICES = 'DEVICES',
    MAP = 'MAPS',
}

export default defineComponent({

    extends: BasePage,

    components: {
        DashboardHeader,

        WidgetMap,
        WidgetStationTable,
        WidgetIndicator,
        WidgetIndicatorOffline,
        WidgetIndicatorTrouble,
        WidgetIndicatorGasAlarm,
        WidgetIndicatorOk,
        WidgetIndicatorReallyOffline,
        DashboardEventsChart,
        DashboardOfflineChart,

        NTab,
        NTabs,
        NTabPane,
    },
    
    unmounted()  {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    },

    data() {

        return  {
            
            resizeListener: null,
            width: window.innerWidth,

            currentTab: TabEnum.ALARMS,
            TabEnum,

            showIndicators: true,
            showMap: true,
            showDevices: true,
            showAlertCount: true,
        }
    },

    
    computed: {
        ...mapStores(useStationStore, useDashboardStore),
        ...mapState(useDashboardStore, ['since', 'before', 'loading']),
        ...mapState(useStationStore, ['groups', 'group', 'stations']),
    },

    async mounted(): Promise<void> {
        const group: string = this.$route.params.group as string;
        const since = this.$route.query.since as string;
        const before = this.$route.query.before as string;

        try {

            await this.stationStore.init();

            if(!this.stationStore.groups?.length) {
                this.$router.push({name: 'wrong-configuration'});
                return;
            }

        } catch (error) {
            if(error instanceof StoreError ) {
                this.$router.push({ name: 'wrong-configuration' });
                return;
            } else {
                throw error;
            }
        }

        try {

            if(group === 'default') {
                this.$router.replace({name: 'dashboard', params: { group: this.stationStore.default.name}});
                return;
            }

            await this.stationStore.selectStationGroup(group);
            
            if(since && before) {
                this.dashboardStore.setTimerange(moment(since), moment(before));
            } else {
                this.dashboardStore.setTimerange(moment().startOf('days').subtract(1, 'months'), moment().endOf('days'))
            }
            
            await this.dashboardStore.refresh();

        } catch (error) {
            if(error instanceof DashboardStoreSocketError) {
                console.error('Could initialize socket ',error);
            } else if(error instanceof StoreError ) {
                console.error('Could not load page',error);
                this.$router.push({ name: 'not-found' });
            } else {
                throw error;
            }
        }

        try {
            this.dashboardStore.initSocket();
        } catch(error) {
            console.error(error);
        }

    },

    watch: {
        async $route(to: {query: LocationQuery, path: string}, from:{query: LocationQuery, path: string}) {
            if(!to.path.includes('dashboard')){
                return;
            }

            let since: Moment = null;
            if(to?.query?.since && to.query?.since !== from?.query?.since) {
                since = moment(to?.query?.since);
            }

            let before: Moment = null;
            if(to?.query?.before && to.query?.before !== from?.query?.before) {
                before = moment(to?.query?.before);
            }

            let refresh: boolean = false;
            if(since || before) {
                this.dashboardStore.setTimerange(since, before);
                refresh = true;
            }
            
            if(this.stationStore.group?.name !== this.$route?.params.group) {
                await this.stationStore.selectStationGroup(this.$route?.params.group as string);
                await this.stationStore.refresh();
                this.dashboardStore.initSocket();
                refresh = true;
            }

            if(refresh) {
                this.dashboardStore.refresh();
            }
        },
    }
})